import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Chip } from "@mui/material";
import { AutoAwesome, Mood, Public } from "@mui/icons-material";

const VIBES = [
  { key: "tenang",   labelKey: "vibeTenang" },
  { key: "kuat",     labelKey: "vibeKuat" },
  { key: "ceria",    labelKey: "vibeCeria" },
  { key: "elegan",   labelKey: "vibeElegan" },
  { key: "religius", labelKey: "vibeReligius" },
  { key: "modern",   labelKey: "vibeModern" },
];

const ORIGINS = [
  { key: "javanese",  labelKey: "originJawa" },
  { key: "sundanese", labelKey: "originSunda" },
  { key: "arabic",    labelKey: "originArab" },
  { key: "malay",     labelKey: "originMelayu" },
  { key: "batak",     labelKey: "originBatak" },
  { key: "balinese",  labelKey: "originBali" },
];

// compact=true  → small chip strip (shown when results are present)
// compact=false → hero layout (shown before first results)
// title         → optional heading override (used for re-discovery nudge)
function DiscoveryPanel({ onSurprise, onVibe, onOrigin, loading, compact = false, title }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null); // null | "vibe" | "origin"

  const handleExpand = (type) => {
    setExpanded(expanded === type ? null : type);
  };

  // ── Compact strip (after results load) ───────────────────────────────────
  if (compact) {
    return (
      <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Chip
          label={t("discoveryStripRetry")}
          onClick={onSurprise}
          disabled={loading}
          color="primary"
          size="small"
          clickable
          sx={{ fontWeight: 600 }}
        />
        {VIBES.map(({ key, labelKey }) => (
          <Chip
            key={key}
            label={t(labelKey)}
            onClick={() => onVibe(key)}
            disabled={loading}
            size="small"
            variant="outlined"
            clickable
          />
        ))}
        {ORIGINS.map(({ key, labelKey }) => (
          <Chip
            key={key}
            label={t(labelKey)}
            onClick={() => onOrigin(key)}
            disabled={loading}
            size="small"
            variant="outlined"
            clickable
          />
        ))}
      </Box>
    );
  }

  // ── Hero layout ───────────────────────────────────────────────────────────
  const heading = title || t("discoveryHeading");

  return (
    <Box sx={{ mb: 0 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {heading}
      </Typography>

      {/* Kejutkan Saya — full-width primary CTA */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<AutoAwesome />}
        onClick={onSurprise}
        disabled={loading}
        sx={{ mb: 2, borderRadius: 2, textTransform: "none", py: 1.5 }}
      >
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="body1" fontWeight={700} component="div">
            {t("discoverySurprise")}
          </Typography>
          <Typography variant="caption" component="div" sx={{ opacity: 0.85 }}>
            {t("discoverySurpriseSubLabel")}
          </Typography>
        </Box>
      </Button>

      {/* Pilih Suasana + Pilih Asal Budaya side-by-side */}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Button
          variant={expanded === "vibe" ? "contained" : "outlined"}
          fullWidth
          startIcon={<Mood />}
          onClick={() => handleExpand("vibe")}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {t("discoveryVibe")}
        </Button>
        <Button
          variant={expanded === "origin" ? "contained" : "outlined"}
          fullWidth
          startIcon={<Public />}
          onClick={() => handleExpand("origin")}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {t("discoveryOrigin")}
        </Button>
      </Box>

      {/* Vibe chips */}
      {expanded === "vibe" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
          {VIBES.map(({ key, labelKey }) => (
            <Chip
              key={key}
              label={t(labelKey)}
              onClick={() => { onVibe(key); setExpanded(null); }}
              disabled={loading}
              color="primary"
              variant="outlined"
              clickable
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Box>
      )}

      {/* Origin chips */}
      {expanded === "origin" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
          {ORIGINS.map(({ key, labelKey }) => (
            <Chip
              key={key}
              label={t(labelKey)}
              onClick={() => { onOrigin(key); setExpanded(null); }}
              disabled={loading}
              color="primary"
              variant="outlined"
              clickable
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default DiscoveryPanel;
